import React, { useState, useEffect, useCallback, useMemo, forwardRef, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes, faExclamation, faInfo, faChevronRight, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useIncident } from "@/Context/Incident/IncidentContext";
import { base64ToString, formatAgencyTimelineDateTime } from "@/Components/Common/Utility";
import {
  calculateNibrsReadinessPercent,
  deriveNibrsReadinessCounts,
  getEmptyFrwNibrsRequiredFlags,
  readNibrsReadinessCountsSnapshotFromStorage,
  readFrwNibrsRequiredFromStorage,
} from "@/Components/Pages/ReportModule/Utils/report-module-frw-nibrs-required.utils";
import { fetchPostData } from "@/Components/hooks/Api";
import { AgencyContext } from "@/Context/Agency";

// ✅ forwardRef use correctly
const StatusBar = forwardRef((props, ref) => {

  const pathname = window.location.pathname;
  const navigate = useNavigate();
  const { isRestricted } = useIncident();
  const { loading, incidentErrorStatus, offenseCount, offenseErrorStatus, NameCount, nameErrorStatus, NameRelationshipError, narrativeApprovedStatus, PropertyCount, VehicleCount, PropErrorStatus, vehErrorStatus } = props;

  const useQuery = () => {
    const params = new URLSearchParams(useLocation().search);
    return {
      get: (param) => params.get(param)
    };
  };

  const query = useQuery();
  var IncID = query?.get("IncId");
  var IncNo = query?.get("IncNo");
  var IncSta = query?.get("IncSta");
  const decodedIncID = IncID ? parseInt(base64ToString(IncID)) : 0;
  const [showNameSuggestionCard, setShowNameSuggestionCard] = useState(false);
  const [showPropertySuggestionLink, setShowPropertySuggestionLink] = useState(false);
  const [showVehicleSuggestionLink, setShowVehicleSuggestionLink] = useState(false);
  const [showArrestSuggestionLink, setShowArrestSuggestionLink] = useState(false);
  const [hasSelectedReportTemplate, setHasSelectedReportTemplate] = useState(false);
  const [reportNarrativeStatus, setReportNarrativeStatus] = useState('');

  // Step 1: FRW API decides which NIBRS links are required for this incident (set on Report Template select).
  const [nibrsFrwRequired, setNibrsFrwRequired] = useState(getEmptyFrwNibrsRequiredFlags());

  // Step 2: merged tag + modal data decides which required items are already present.
  const [nibrsDataPresent, setNibrsDataPresent] = useState({
    victim: false,
    societyVictim: false,
    offender: false,
    property: false,
    drugProperty: false,
    vehicle: false,
    arrest: false,
  });
  const [nibrsReadinessCounts, setNibrsReadinessCounts] = useState({
    resolvedCount: 0,
    totalCount: 0,
  });

  const [reportHistory, setReportHistory] = useState([]);
  const [expandedReport, setExpandedReport] = useState(null);
  const [isNewFRW, setIsNewFRW] = useState(false);  // true for new FRW, false for old FRW it will based on the Agency
  const { agencySingleData, GetAgencySingleData } = useContext(AgencyContext);

  useEffect(() => {
    setIsNewFRW(agencySingleData[0]?.isNewFRW);
  }, [agencySingleData]);

  useEffect(() => {
    if (!decodedIncID) {
      setShowNameSuggestionCard(false);
      setShowPropertySuggestionLink(false);
      setShowVehicleSuggestionLink(false);
      setShowArrestSuggestionLink(false);
      setHasSelectedReportTemplate(false);
      setReportNarrativeStatus('');
      setNibrsFrwRequired(getEmptyFrwNibrsRequiredFlags());
      setNibrsDataPresent({
        victim: false,
        societyVictim: false,
        offender: false,
        property: false,
        drugProperty: false,
        vehicle: false,
        arrest: false,
      });
      setNibrsReadinessCounts({ resolvedCount: 0, totalCount: 0 });
      return;
    }

    setShowNameSuggestionCard(sessionStorage.getItem(`report-name-suggestion-${decodedIncID}`) === "true");
    setShowPropertySuggestionLink(sessionStorage.getItem(`report-property-suggestion-${decodedIncID}`) === "true");
    setShowVehicleSuggestionLink(sessionStorage.getItem(`report-vehicle-suggestion-${decodedIncID}`) === "true");
    setShowArrestSuggestionLink(sessionStorage.getItem(`report-arrest-suggestion-${decodedIncID}`) === "true");
    setHasSelectedReportTemplate(sessionStorage.getItem(`report-template-selected-${decodedIncID}`) === "true");
    setReportNarrativeStatus(sessionStorage.getItem(`report-narrative-status-${decodedIncID}`) || '');
    setNibrsFrwRequired(readFrwNibrsRequiredFromStorage(decodedIncID));
    setNibrsReadinessCounts(readNibrsReadinessCountsSnapshotFromStorage(decodedIncID));
    setNibrsDataPresent({
      victim: sessionStorage.getItem(`report-nibrs-victim-present-${decodedIncID}`) === "true",
      societyVictim: sessionStorage.getItem(`report-nibrs-society-victim-present-${decodedIncID}`) === "true",
      offender: sessionStorage.getItem(`report-nibrs-offender-present-${decodedIncID}`) === "true",
      property: sessionStorage.getItem(`report-nibrs-property-present-${decodedIncID}`) === "true",
      drugProperty: sessionStorage.getItem(`report-nibrs-drug-property-present-${decodedIncID}`) === "true",
      vehicle: sessionStorage.getItem(`report-nibrs-vehicle-present-${decodedIncID}`) === "true",
      arrest: sessionStorage.getItem(`report-nibrs-arrest-present-${decodedIncID}`) === "true",
    });

    const onSuggestionUpdated = (event) => {
      if (event?.detail?.incidentId !== decodedIncID) return;
      const d = event.detail;
      setShowNameSuggestionCard(Boolean(d.hasNameSuggestion));
      setShowPropertySuggestionLink(Boolean(d.hasPropertySuggestion));
      setShowVehicleSuggestionLink(Boolean(d.hasVehicleSuggestion));
      setShowArrestSuggestionLink(Boolean(d.hasArrestSuggestion));
    };

    // Step 1 listener: FRW API response after Report Template select.
    const onFrwNibrsRequiredUpdated = (event) => {
      if (event?.detail?.incidentId !== decodedIncID) return;
      setNibrsFrwRequired({
        showNibrsSection: Boolean(event?.detail?.showNibrsSection),
        requireVictim: Boolean(event?.detail?.requireVictim),
        requireSocietyVictim: Boolean(event?.detail?.requireSocietyVictim),
        requireOffender: Boolean(event?.detail?.requireOffender),
        requireProperty: Boolean(event?.detail?.requireProperty),
        requireDrugProperty: Boolean(event?.detail?.requireDrugProperty),
        requireVehicle: Boolean(event?.detail?.requireVehicle),
        requireArrest: Boolean(event?.detail?.requireArrest),
        errorCodes: Array.isArray(event?.detail?.errorCodes) ? event.detail.errorCodes : [],
        errorMsg: String(event?.detail?.errorMsg || ''),
      });
    };

    // Step 2 listeners: hide a link when merged data shows that item already exists.
    const onVictimPresenceUpdated = (event) => {
      if (event?.detail?.incidentId !== decodedIncID) return;
      setNibrsDataPresent((prev) => ({ ...prev, victim: Boolean(event?.detail?.hasVictimNamePresent) }));
    };
    const onSocietyVictimPresenceUpdated = (event) => {
      if (event?.detail?.incidentId !== decodedIncID) return;
      setNibrsDataPresent((prev) => ({ ...prev, societyVictim: Boolean(event?.detail?.hasSocietyVictimNamePresent) }));
    };

    const onOffenderPresenceUpdated = (event) => {
      if (event?.detail?.incidentId !== decodedIncID) return;
      setNibrsDataPresent((prev) => ({ ...prev, offender: Boolean(event?.detail?.hasOffenderNamePresent) }));
    };

    const onPropertyPresenceUpdated = (event) => {
      if (event?.detail?.incidentId !== decodedIncID) return;
      setNibrsDataPresent((prev) => ({ ...prev, property: Boolean(event?.detail?.hasPropertyPresent) }));
    };

    const onDrugPropertyPresenceUpdated = (event) => {
      if (event?.detail?.incidentId !== decodedIncID) return;
      setNibrsDataPresent((prev) => ({ ...prev, drugProperty: Boolean(event?.detail?.hasDrugPropertyPresent) }));
    };

    const onVehiclePresenceUpdated = (event) => {
      if (event?.detail?.incidentId !== decodedIncID) return;
      setNibrsDataPresent((prev) => ({ ...prev, vehicle: Boolean(event?.detail?.hasVehiclePresent) }));
    };

    const onArrestPresenceUpdated = (event) => {
      if (event?.detail?.incidentId !== decodedIncID) return;
      setNibrsDataPresent((prev) => ({ ...prev, arrest: Boolean(event?.detail?.hasArrestPresent) }));
    };

    window.addEventListener("report-name-suggestion-updated", onSuggestionUpdated);
    window.addEventListener("report-nibrs-frw-required-updated", onFrwNibrsRequiredUpdated);
    window.addEventListener("report-nibrs-victim-presence-updated", onVictimPresenceUpdated);
    window.addEventListener("report-nibrs-society-victim-presence-updated", onSocietyVictimPresenceUpdated);
    window.addEventListener("report-nibrs-offender-presence-updated", onOffenderPresenceUpdated);
    window.addEventListener("report-nibrs-property-presence-updated", onPropertyPresenceUpdated);
    window.addEventListener("report-nibrs-drug-property-presence-updated", onDrugPropertyPresenceUpdated);
    window.addEventListener("report-nibrs-vehicle-presence-updated", onVehiclePresenceUpdated);
    window.addEventListener("report-nibrs-arrest-presence-updated", onArrestPresenceUpdated);
    const onTemplateSelectionUpdated = (event) => {
      if (event?.detail?.incidentId !== decodedIncID) return;
      setHasSelectedReportTemplate(Boolean(event?.detail?.hasSelectedTemplate));
    };

    const onNarrativeStatusUpdated = (event) => {
      if (event?.detail?.incidentId !== decodedIncID) return;
      setReportNarrativeStatus(String(event?.detail?.status || '').trim());
    };
    const onReadinessCountsUpdated = (event) => {
      if (event?.detail?.incidentId !== decodedIncID) return;
      const resolvedCount = Number.parseInt(event?.detail?.resolvedCount, 10);
      const totalCount = Number.parseInt(event?.detail?.totalCount, 10);
      setNibrsReadinessCounts({
        resolvedCount: Number.isFinite(resolvedCount) && resolvedCount >= 0 ? resolvedCount : 0,
        totalCount: Number.isFinite(totalCount) && totalCount >= 0 ? totalCount : 0,
      });
    };

    window.addEventListener("report-template-selection-updated", onTemplateSelectionUpdated);
    window.addEventListener("report-narrative-status-updated", onNarrativeStatusUpdated);
    window.addEventListener("report-nibrs-readiness-counts-updated", onReadinessCountsUpdated);
    return () => {
      window.removeEventListener("report-name-suggestion-updated", onSuggestionUpdated);
      window.removeEventListener("report-nibrs-frw-required-updated", onFrwNibrsRequiredUpdated);
      window.removeEventListener("report-nibrs-victim-presence-updated", onVictimPresenceUpdated);
      window.removeEventListener("report-nibrs-society-victim-presence-updated", onSocietyVictimPresenceUpdated);
      window.removeEventListener("report-nibrs-offender-presence-updated", onOffenderPresenceUpdated);
      window.removeEventListener("report-nibrs-property-presence-updated", onPropertyPresenceUpdated);
      window.removeEventListener("report-nibrs-drug-property-presence-updated", onDrugPropertyPresenceUpdated);
      window.removeEventListener("report-nibrs-vehicle-presence-updated", onVehiclePresenceUpdated);
      window.removeEventListener("report-nibrs-arrest-presence-updated", onArrestPresenceUpdated);
      window.removeEventListener("report-template-selection-updated", onTemplateSelectionUpdated);
      window.removeEventListener("report-narrative-status-updated", onNarrativeStatusUpdated);
      window.removeEventListener("report-nibrs-readiness-counts-updated", onReadinessCountsUpdated);
    };
  }, [decodedIncID]);

  // Fetch report history when on the Report tab
  const fetchReportHistory = useCallback(() => {
    if (!decodedIncID || pathname !== '/Inc-Report') return;
    fetchPostData('IncidentNarrativeReport/GetNarrativeReportLeftTreeHistory', {
      IncidentID: decodedIncID,
      UseofForceID: 0,
      IsCase: false,
    }).then((res) => {
      if (res) {
        try {
          const raw = res[0]?.ReportHistory;
          const parsed = typeof raw === 'string' ? JSON.parse(raw) : (Array.isArray(raw) ? raw : []);
          setReportHistory(Array.isArray(parsed) ? parsed : []);
        } catch {
          setReportHistory([]);
        }
      } else {
        setReportHistory([]);
      }
    });
  }, [decodedIncID, pathname]);

  useEffect(() => {
    fetchReportHistory();
  }, [fetchReportHistory]);

  useEffect(() => {
    if (pathname !== '/Inc-Report') return;
    const onRefresh = (e) => {
      if (e?.detail?.incidentId !== decodedIncID) return;
      fetchReportHistory();
    };
    window.addEventListener('report-history-refresh', onRefresh);
    return () => window.removeEventListener('report-history-refresh', onRefresh);
  }, [decodedIncID, pathname, fetchReportHistory]);

  const getLatestReportStatus = (statuses) =>
    statuses?.[statuses.length - 1]?.status || 'Draft';

  const effectiveNarrativeApprovedStatus = useMemo(() => {
    if (pathname !== '/Inc-Report' || reportHistory.length === 0) {
      return narrativeApprovedStatus;
    }
    return reportHistory.every(
      (report) => getLatestReportStatus(report.Status).toLowerCase() === 'approved',
    );
  }, [pathname, reportHistory, narrativeApprovedStatus]);

  const statusData = [
    {
      label: "Incident",
      value: loading ? "Loading..." : incidentErrorStatus ? "Error" : "Completed",
      border: incidentErrorStatus ? "left-border-red" : "left-border-green",
      color: "text-grays",
      bg: incidentErrorStatus ? "bg-red" : "bg-green",
      icon: incidentErrorStatus ? faTimes : faCheck,
      link: `/Inc-Home?IncId=${IncID}&IncNo=${IncNo}&IncSta=${IncSta}`,
    },
    {
      label: "Offense",
      value: loading ? "Loading..." : offenseCount && !offenseErrorStatus ? `Added` : `${offenseCount} Added`,
      // value: loading ? "Loading..." : !offenseErrorStatus ? `${offenseCount} Added` : "0/1",
      border: offenseCount && !offenseErrorStatus ? "left-border-green" : "left-border-red",
      color: "text-grays",
      bg: offenseCount && !offenseErrorStatus ? "bg-green" : "bg-red",
      icon: offenseCount && !offenseErrorStatus ? faCheck : faTimes,
      link: `/Off-Home?IncId=${IncID}&IncNo=${IncNo}&IncSta=${IncSta}`,
    },
    {
      label: "Name",
      value: loading ? "Loading..." : NameCount && !nameErrorStatus ? "Added" : `${NameCount} Added`,
      border: NameCount && !nameErrorStatus ? "left-border-green" : "left-border-red",
      color: "text-grays",
      bg: NameCount && !nameErrorStatus ? "bg-green" : "bg-red",
      icon: NameCount && !nameErrorStatus ? faCheck : faTimes,
      link: `/Name-Home?IncId=${IncID}&IncNo=${IncNo}&IncSta=${IncSta}`,
    },
    {
      label: "OV Links",
      value: loading ? "Loading..." : !NameRelationshipError ? "0/0 Linked" : "Linked",
      border: !NameRelationshipError ? "left-border-red" : "left-border-green",
      color: "text-grays",
      bg: !NameRelationshipError ? "bg-red" : "bg-green",
      icon: !NameRelationshipError ? faTimes : faCheck,
      link: `/Offvic-Home?IncId=${IncID}&IncNo=${IncNo}&IncSta=${IncSta}`,
    },
    {
      label: "Report",
      value: loading ? "Loading..." : effectiveNarrativeApprovedStatus ? "Approved" : "Not Approved",
      border: effectiveNarrativeApprovedStatus ? "left-border-green" : "left-border-yellow",
      color: "text-grays",
      bg: effectiveNarrativeApprovedStatus ? "bg-green" : "bg-yellow",
      icon: effectiveNarrativeApprovedStatus ? faCheck : faExclamation,
      link: `/Inc-Report?IncId=${IncID}&IncNo=${IncNo}&IncSta=${IncSta}`,
    },
    ...(showNameSuggestionCard ? [{
      label: "Name",
      value: "Suggestion Found",
      border: "left-border-yellow",
      color: "text-grays",
      bg: "bg-yellow",
      icon: faInfo,
      link: `/Name-Home?IncId=${IncID}&IncNo=${IncNo}&IncSta=${IncSta}`,
      isNameSuggestion: true,
    }] : []),
    // {
    //   label: "Property",
    //   value: loading ? "Loading..." : PropertyCount && !PropErrorStatus ? "Added" : `${PropertyCount} Added`,
    //   border: PropertyCount && !PropErrorStatus ? "left-border-green" : "left-border-red",
    //   color: "text-grays",
    //   bg: PropertyCount && !PropErrorStatus ? "bg-green" : "bg-red",
    //   icon: PropertyCount && !PropErrorStatus ? faCheck : faTimes,
    // },
    {
      label: "Property",
      value: loading ? "Loading..." : typeof PropertyCount === "number" && !PropErrorStatus ? `${pathname === '/Vehicle-Home' ? VehicleCount : PropertyCount} Added` : `${pathname === '/Vehicle-Home' ? VehicleCount : PropertyCount} Added`,
      border: typeof PropertyCount === "number" && !PropErrorStatus ? "left-border-green" : "left-border-red",
      bg: typeof PropertyCount === "number" && !PropErrorStatus ? "bg-green" : "bg-red",
      icon: typeof PropertyCount === "number" && !PropErrorStatus ? faCheck : faTimes,
      link: `/Prop-Home?IncId=${IncID}&IncNo=${IncNo}&IncSta=${IncSta}`,
    },
    {
      label: "Vehicle",
      value: loading ? "Loading..." : typeof VehicleCount === "number" && !vehErrorStatus ? `${VehicleCount} Added` : `${VehicleCount} Added`,
      border: typeof VehicleCount === "number" && !vehErrorStatus ? "left-border-green" : "left-border-red",
      bg: typeof VehicleCount === "number" && !vehErrorStatus ? "bg-green" : "bg-red",
      icon: typeof VehicleCount === "number" && !vehErrorStatus ? faCheck : faTimes,
      link: `/Vehicle-Home?IncId=${IncID}&IncNo=${IncNo}&IncSta=${IncSta}`,
    },
    {
      label: "TIBRS",
      value: "Pending",
      color: "text-grays",
      // icon: faInfo,
    },
  ];

  const getFilteredStatusData = (pathname) => {
    switch (pathname) {
      case "/Off-Home":
        return statusData.filter(item => item.label === "Offense");
      case "/Name-Home":
      case "/Offvic-Home":
        return statusData.filter(item => item.label === "Name" || item.label === "OV Links");
      case "/Prop-Home":
        return statusData.filter(item => item.label === "Property");
      case "/Vehicle-Home":
        return statusData.filter(item => item.label === "Vehicle");
      case "/Inc-Report":
        return statusData.filter(item => item.label === "Report" || item.isNameSuggestion);
      case "/Arrest-Home":
      case "/Document-Home":
      case "/NIBRSAudit-Home":
      case "/CloseHistory-Home":
      case "/NLETShistory-Home":
        return []
      default:
        return statusData;
    }
  };


  //# START: FOR REPORT MODULE
  const isReportTab = pathname === "/Inc-Report";

  const showReportQuickNavSection =
    isReportTab &&
    (showNameSuggestionCard ||
      showPropertySuggestionLink ||
      showVehicleSuggestionLink ||
      showArrestSuggestionLink);

  const reportQuickNavLinks = [
    showNameSuggestionCard
      ? {
        id: "name",
        label: "Name",
        type: "nameEntry",
      }
      : null,
    showPropertySuggestionLink
      ? {
        id: "property",
        label: "Property",
        link: `/Prop-Home?IncId=${IncID}&IncNo=${IncNo}&IncSta=${IncSta}`,
      }
      : null,
    showVehicleSuggestionLink
      ? {
        id: "vehicle",
        label: "Vehicle",
        type: "vehicleEntry",
      }
      : null,
    showArrestSuggestionLink
      ? {
        id: "arrest",
        label: "Arrest",
        type: "arrestEntry",
      }
      : null,
  ].filter(Boolean);

  const openNameEntryFromReportSidebar = ({ type } = {}) => {
    if (isRestricted || !decodedIncID) return;
    window.dispatchEvent(
      new CustomEvent("report-open-name-entry-modal", {
        detail: { incidentId: decodedIncID, ...(type ? { type } : {}) },
      })
    );
  };

  const openArrestEntryFromReportSidebar = () => {
    if (isRestricted || !decodedIncID) return;
    window.dispatchEvent(
      new CustomEvent("report-open-arrest-entry-modal", {
        detail: { incidentId: decodedIncID },
      })
    );
  };

  const openVehicleEntryFromReportSidebar = () => {
    if (isRestricted || !decodedIncID) return;
    window.dispatchEvent(
      new CustomEvent("report-open-vehicle-entry-modal", {
        detail: { incidentId: decodedIncID },
      })
    );
  };

  const openPropertyEntryFromReportSidebar = ({ type } = {}) => {
    if (isRestricted || !decodedIncID) return;
    window.dispatchEvent(
      new CustomEvent("report-open-property-entry-modal", {
        detail: { incidentId: decodedIncID, ...(type ? { type } : {}) },
      })
    );
  };

  const isReportNarrativeAddEntriesHidden =
    reportNarrativeStatus === 'Pending Review'
    || reportNarrativeStatus === 'Pending'
    || reportNarrativeStatus === 'Approved';
  const showReportEntrySection = isReportTab && hasSelectedReportTemplate;
  const showReportAddNewEntriesSection =
    showReportEntrySection && !isReportNarrativeAddEntriesHidden;
  const showReportNibrsSection =
    showReportEntrySection && nibrsFrwRequired.showNibrsSection && !isReportNarrativeAddEntriesHidden;

  // Final NIBRS visibility = FRW API required AND item not already present in merged data.
  const showNibrsFrwSection = nibrsFrwRequired.showNibrsSection;
  const derivedReadinessFromPresence = deriveNibrsReadinessCounts(nibrsFrwRequired, nibrsDataPresent, nibrsReadinessCounts.totalCount);
  const showSocietyVictimNibrsLink = nibrsFrwRequired.requireSocietyVictim && !nibrsDataPresent.societyVictim;
  const showVictimNameNibrsLink = nibrsFrwRequired.requireVictim && !nibrsDataPresent.victim;
  const showOffenderNameNibrsLink = nibrsFrwRequired.requireOffender && !nibrsDataPresent.offender;
  const effectiveRequireProperty = derivedReadinessFromPresence.effectiveRequireProperty;
  const showPropertyNibrsLink = effectiveRequireProperty && !nibrsDataPresent.property;
  const showDrugPropertyNibrsLink = nibrsFrwRequired.requireDrugProperty && !nibrsDataPresent.drugProperty;
  const showVehicleNibrsLink = nibrsFrwRequired.requireVehicle && !nibrsDataPresent.vehicle;
  const showArrestNibrsLink = nibrsFrwRequired.requireArrest && !nibrsDataPresent.arrest;

  const totalNibrsRequiredCount = nibrsReadinessCounts.totalCount > 0
    ? nibrsReadinessCounts.totalCount
    : derivedReadinessFromPresence.totalCount;
  const resolvedNibrsRequiredCount = nibrsReadinessCounts.totalCount > 0
    ? Math.min(
      Math.max(nibrsReadinessCounts.resolvedCount, derivedReadinessFromPresence.resolvedCount),
      totalNibrsRequiredCount,
    )
    : derivedReadinessFromPresence.resolvedCount;
  const unresolvedNibrsRequiredCount = Math.max(totalNibrsRequiredCount - resolvedNibrsRequiredCount, 0);
  const nibrsProgressPercent = calculateNibrsReadinessPercent(
    resolvedNibrsRequiredCount,
    totalNibrsRequiredCount,
  );

  const nibrsProgressToneClass = nibrsProgressPercent <= 25
    ? "is-low"
    : nibrsProgressPercent <= 50
      ? "is-medium"
      : nibrsProgressPercent <= 75
        ? "is-good"
        : "is-high";

  const hasNibrsErrorCodes = Array.isArray(nibrsFrwRequired.errorCodes) && nibrsFrwRequired.errorCodes.length > 0;
  const hasVisibleNibrsCards = unresolvedNibrsRequiredCount > 0;
  const isReportApprovedStatus =
    effectiveNarrativeApprovedStatus ||
    reportNarrativeStatus.toLowerCase() === "approved";

  const showNibrsProgressCard =
    isReportTab &&
    showReportEntrySection &&
    hasNibrsErrorCodes &&
    !isReportApprovedStatus &&
    totalNibrsRequiredCount > 0;
  //# END: FOR REPORT MODULE
  const STATUS_COLORS = {
    approved: { solid: '#28a745', bg: '#eaf6ed', text: '#1a6b31', border: '#a3d9af' },
    'pending review': { solid: '#007bff', bg: '#e7f1ff', text: '#0056b3', border: '#99caff' },
    draft: { solid: '#ffc107', bg: '#fff9e6', text: '#856404', border: '#ffe08a' },
    reject: { solid: '#FF0000', bg: '#ffe6e6', text: '#cc0000', border: '#ffaaaa' },
    rejected: { solid: '#FF0000', bg: '#ffe6e6', text: '#cc0000', border: '#ffaaaa' },
  };

  const getStatusColor = (statuses) => {
    const last = statuses?.[statuses.length - 1]?.status?.toLowerCase();
    return STATUS_COLORS[last] || STATUS_COLORS['draft'];
  };

  const getStatusDotColor = (statuses) => getStatusColor(statuses).solid;

  const getStatusBadgeStyle = (statuses) => {
    const c = getStatusColor(statuses);
    return { bg: c.bg, color: c.text, border: c.border };
  };

  const getLatestStatus = (statuses) => statuses?.[statuses.length - 1]?.status || 'Draft';

  return (
    <div ref={ref} className="d-flex flex-column gap-3 mb-2 py-3 px-2" style={{
      rowGap: "6px",
      backgroundColor: "#fff",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
      borderRadius: "6px",
    }}>
      {showNibrsProgressCard && isNewFRW ? (
        <div className="nibrs-progress-card">
          <div className="nibrs-progress-card__top">
            <span className="nibrs-progress-card__label">NIBRS READINESS · A</span>
            <span className="nibrs-progress-card__percent">{nibrsProgressPercent}%</span>
            <span className="nibrs-progress-card__ratio">{resolvedNibrsRequiredCount} / {totalNibrsRequiredCount}</span>
          </div>
          <div className={`nibrs-progress-card__segments ${nibrsProgressToneClass}`} aria-hidden="true">
            {Array.from({ length: totalNibrsRequiredCount }).map((_, idx) => (
              <span
                key={idx}
                className={`nibrs-progress-card__segment ${idx < resolvedNibrsRequiredCount ? "is-active" : ""}`}
              />
            ))}
          </div>
          <div className="nibrs-progress-card__note">
            {unresolvedNibrsRequiredCount} required {unresolvedNibrsRequiredCount === 1 ? "entry" : "entries"} missing
          </div>
        </div>
      ) : null}

      {getFilteredStatusData(pathname).map((item, index) => (
        <div
          key={index}
          className={`d-flex flex-column status-card ${item.border}`}
          onClick={() => {
            if (isRestricted) {
              return;
            }
            if (pathname === item.link?.split("?")[0] || item.label === "TIBRS") {
              return;
            } else {
              navigate(item.link)
            }
          }}
          style={{
            cursor: isRestricted ? "not-allowed" : "pointer",
            opacity: isRestricted ? 0.6 : 1,
          }}
        >
          <div className="d-flex align-items-center justify-content-between ">
            {/* Left side */}
            <div className="d-flex flex-column">
              <div className="status-label">{item.label}</div>
              <div className={`status-value ${item.color}`}>{item.value}</div>
            </div>

            {/* Right side */}
            <div className={`status-icons ${item.bg}`}>
              <FontAwesomeIcon icon={item.icon} />
            </div>
          </div>
        </div>
      ))}

      {showReportAddNewEntriesSection && isNewFRW ? (
        <div className="report-entry-actions px-1 pt-2">
          <hr className="report-quick-nav-rule" />
          <p className="text-muted small mb-0 text-uppercase" style={{ fontSize: "12px", fontWeight: "700" }}>Add Manually</p>
          <div className="report-entry-actions-links pt-2">
            <button
              type="button"
              className="report-entry-action-btn w-100"
              disabled={isRestricted}
              onClick={() => openNameEntryFromReportSidebar()}
            >
              <span className="report-entry-action-label">Name</span>
              <span className="report-entry-action-icon"><i className="fa fa-plus"></i></span>
            </button>
            <button
              type="button"
              className="report-entry-action-btn w-100"
              disabled={isRestricted}
              onClick={() => openPropertyEntryFromReportSidebar()}
            >
              <span className="report-entry-action-label">Property</span>
              <span className="report-entry-action-icon"><i className="fa fa-plus"></i></span>
            </button>
            <button
              type="button"
              className="report-entry-action-btn w-100"
              disabled={isRestricted}
              onClick={openVehicleEntryFromReportSidebar}
            >
              <span className="report-entry-action-label">Vehicle</span>
              <span className="report-entry-action-icon"><i className="fa fa-plus"></i></span>
            </button>
            <button
              type="button"
              className="report-entry-action-btn w-100"
              disabled={isRestricted}
              onClick={openArrestEntryFromReportSidebar}
            >
              <span className="report-entry-action-label">Arrest</span>
              <span className="report-entry-action-icon"><i className="fa fa-plus"></i></span>
            </button>
          </div>
        </div>
      ) : null}

      {showReportQuickNavSection && isNewFRW ? (
        <div className="report-quick-nav px-1 pt-2">
          <hr className="report-quick-nav-rule" />
          <p className="text-muted small mb-0 text-uppercase" style={{ fontSize: "12px", fontWeight: "700" }}>Ai Suggested Quick Links</p>
          <div className="report-quick-nav-links pt-2">
            {reportQuickNavLinks.map((link) => (
              <button
                key={link.id}
                type="button"
                className="report-quick-nav-link w-100"
                disabled={isRestricted}
                onClick={() => {
                  if (isRestricted) return;
                  if (link.type === "nameEntry") {
                    openNameEntryFromReportSidebar();
                    return;
                  }
                  if (link.type === "arrestEntry") {
                    openArrestEntryFromReportSidebar();
                    return;
                  }
                  if (link.type === "vehicleEntry") {
                    openVehicleEntryFromReportSidebar();
                    return;
                  }
                  if (link.link && pathname !== link.link.split("?")[0]) {
                    navigate(link.link);
                  }
                }}
              >
                {link.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {/* FOR NIBRS MODAL FORM ACTIONS — shown only when FRW API returns at least one ErrorCode */}
      {showReportNibrsSection && isNewFRW ? (
        <div className="report-nibrs-actions px-1">
        <hr className="report-quick-nav-rule" />
        <p className="text-muted small mb-0 text-uppercase" style={{ fontSize: "12px", fontWeight: "700" }}>NIBRS</p>
        <div className="report-nibrs-actions-links pt-2">
          {showVictimNameNibrsLink ? (
            <div
              className={`nibrs-card w-100 ${isRestricted ? "nibrs-card-disabled" : ""}`}
              onClick={() => openNameEntryFromReportSidebar({ type: "victim" })}
            >
              <div className="nibrs-card-header">
                <span className="nibrs-card-title">Victim</span>
                <span className="nibrs-tag">NIBRS</span>
              </div>
              <p className="nibrs-card-description">Required Crime Against Person</p>
              <button type="button" className="nibrs-card-button" disabled={isRestricted} onClick={(e) => { e.stopPropagation(); openNameEntryFromReportSidebar({ type: "victim" }); }}>Add now</button>
            </div>
          ) : null}
          {showSocietyVictimNibrsLink ? (
            <div
              className={`nibrs-card w-100 ${isRestricted ? "nibrs-card-disabled" : ""}`}
              onClick={() => openNameEntryFromReportSidebar({ type: "societyVictim" })}
            >
              <div className="nibrs-card-header">
                <span className="nibrs-card-title">Society Victim</span>
                <span className="nibrs-tag">NIBRS</span>
              </div>
              <p className="nibrs-card-description">Required Crime Against Society</p>
              <button
                type="button"
                className="nibrs-card-button"
                disabled={isRestricted}
                onClick={(e) => { e.stopPropagation(); openNameEntryFromReportSidebar({ type: "societyVictim" }); }}
              >
                Add now
              </button>
            </div>
          ) : null}
          {showOffenderNameNibrsLink ? (
          <div
            className={`nibrs-card w-100 ${isRestricted ? "nibrs-card-disabled" : ""}`}
            onClick={() => openNameEntryFromReportSidebar({ type: "offender" })}
          >
            <div className="nibrs-card-header">
              <span className="nibrs-card-title">Offender</span>
              <span className="nibrs-tag">NIBRS</span>
            </div>
            <p className="nibrs-card-description">Required Crime Against Person</p>
            <button type="button" className="nibrs-card-button" disabled={isRestricted} onClick={(e) => { e.stopPropagation(); openNameEntryFromReportSidebar({ type: "offender" }); }}>Add now</button>
          </div>
          ) : null}
          {showPropertyNibrsLink ? (
          <div
            className={`nibrs-card w-100 ${isRestricted ? "nibrs-card-disabled" : ""}`}
            onClick={() => openPropertyEntryFromReportSidebar()}
          >
            <div className="nibrs-card-header">
              <span className="nibrs-card-title">Property</span>
              <span className="nibrs-tag">NIBRS</span>
            </div>
            <p className="nibrs-card-description">Required Crime Against Property</p>
            <button type="button" className="nibrs-card-button" disabled={isRestricted} onClick={(e) => { e.stopPropagation(); openPropertyEntryFromReportSidebar(); }}>Add now</button>
          </div>
          ) : null}
          {showDrugPropertyNibrsLink ? (
          <div
            className={`nibrs-card w-100 ${isRestricted ? "nibrs-card-disabled" : ""}`}
            onClick={() => openPropertyEntryFromReportSidebar({ type: "drug" })}
          >
            <div className="nibrs-card-header">
              <span className="nibrs-card-title">Drug Property</span>
              <span className="nibrs-tag">NIBRS</span>
            </div>
            <p className="nibrs-card-description">Required by 35A</p>
            <button type="button" className="nibrs-card-button" disabled={isRestricted} onClick={(e) => { e.stopPropagation(); openPropertyEntryFromReportSidebar({ type: "drug" }); }}>Add now</button>
          </div>
          ) : null}
          {showVehicleNibrsLink ? (
          <div
            className={`nibrs-card w-100 ${isRestricted ? "nibrs-card-disabled" : ""}`}
            onClick={openVehicleEntryFromReportSidebar}
          >
            <div className="nibrs-card-header">
              <span className="nibrs-card-title">Vehicle</span>
              <span className="nibrs-tag">NIBRS</span>
            </div>
            <p className="nibrs-card-description">Required by 240</p>
            <button type="button" className="nibrs-card-button" disabled={isRestricted} onClick={(e) => { e.stopPropagation(); openVehicleEntryFromReportSidebar(); }}>Add now</button>
            </div>
          ) : null}
          {showArrestNibrsLink ? (
          <div
            className={`nibrs-card w-100 ${isRestricted ? "nibrs-card-disabled" : ""}`}
            onClick={openArrestEntryFromReportSidebar}
          >
            <div className="nibrs-card-header">
              <span className="nibrs-card-title">Arrest</span>
              <span className="nibrs-tag">NIBRS</span>
            </div>
            <p className="nibrs-card-description">Required by group of (B) offenses</p>
            <button type="button" className="nibrs-card-button" disabled={isRestricted} onClick={(e) => { e.stopPropagation(); openArrestEntryFromReportSidebar(); }}>Add now</button>
          </div>
          ) : null}
        </div>
      </div>
      ) : null}
      {/* ── Report History ── */}
      {isReportTab && reportHistory.length > 0 && (
        <div style={{ marginTop: 2 }}>
          <hr style={{ margin: '4px 0 8px' }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <p className="text-muted small mb-0 text-uppercase" style={{ fontSize: "12px", fontWeight: "700" }}>Reports</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto', paddingRight: 2 }}>
            {reportHistory.map((report, idx) => {
              const dotColor = getStatusDotColor(report.Status);
              const badge = getStatusBadgeStyle(report.Status);
              const latestStatus = getLatestStatus(report.Status);
              const isExpanded = expandedReport === idx;

              return (
                <div key={idx} style={{ borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff' }}  >
                  {/* Header row */}
                  <span onClick={() => setExpandedReport(isExpanded ? null : idx)} style={{ cursor: 'pointer' }}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 4px' }}
                  >
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: dotColor, flexShrink: 0, display: 'inline-block' }} />
                    <span
                      title={report.reportType === "Use Of Force" ? (report.isUseOfForceArrest ? `Arrest ${report.reportType}` : `Inc ${report.reportType}`) : report.reportType}
                      style={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {
                        report.reportType === "Use Of Force" ?
                          report.isUseOfForceArrest ? `Arrest ${report.reportType}` : `Inc ${report.reportType}`
                          : report.reportType
                      }
                    </span>
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '1px 2px', borderRadius: 3, flexShrink: 0,
                      background: badge.bg, color: badge.color, border: `1px solid ${badge.border}`
                    }}>
                      {latestStatus}
                    </span>
                    <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} style={{ fontSize: 9, color: '#9ca3af', flexShrink: 0 }} />
                  </div>

                  {/* Meta — always visible */}
                  <div style={{ padding: '0 5px 4px', fontSize: 11, color: '#6b7280', lineHeight: 1.7 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: '600' }}>Written For</span>
                      <span style={{ fontWeight: '700' }}>{report.WrittenFor_Officer}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: '600' }}>Written Date</span>
                      <span style={{ fontWeight: '700' }}>{formatAgencyTimelineDateTime(report.Written)} </span>
                    </div>
                    {report.approverName !== "Approver not assigned" && <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: '600' }}>Approver</span>
                      <span style={{ fontWeight: '700' }}>{report.approverName || 'Not assigned'}</span>
                    </div>}
                  </div>
                  </span>

                  {/* Expandable workflow timeline */}
                  {isExpanded && (
                    <div style={{ borderTop: '1px solid #f3f4f6', padding: '8px 10px 10px' }}>
                      {/* <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: '#374151', textTransform: 'uppercase', marginBottom: 8 }}>
                        Approval Workflow
                      </div> */}
                      <div style={{ position: 'relative', paddingLeft: 24 }}>
                        {/* vertical connector line */}
                        <div style={{ position: 'absolute', left: 7, top: 8, bottom: 8, width: 2, background: '#e5e7eb' }} />
                        {(report.Status || []).map((step, sIdx) => {
                          const s = step.status?.toLowerCase();
                          const sc = STATUS_COLORS[s] || STATUS_COLORS['draft'];
                          const dotBg = sc.solid;
                          const dotShadow = `0 0 0 3px ${sc.border}`;
                          const dotIcon =
                            s === 'approved' ? faCheck :
                              s === 'reject' || s === 'rejected' ? faTimes :
                                null;
                          return (
                            <div key={sIdx} style={{ display: 'flex', gap: 8, marginBottom: sIdx < report.Status.length - 1 ? 10 : 0, position: 'relative', zIndex: 1 }}>
                              <div style={{
                                position: 'absolute', left: -24,
                                width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: dotBg,
                                color: '#fff', fontSize: 8,
                                boxShadow: dotShadow,
                              }}>
                                {dotIcon && <FontAwesomeIcon icon={dotIcon} />}
                              </div>
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>{step.status}</div>
                                <div style={{ fontSize: 11 }}>{formatAgencyTimelineDateTime(step.Date)}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

export default StatusBar;
